import type { FolderUploadPanelCopy } from './folder-upload-panel-copy';

/** Upload panel copy for Latin- and Cyrillic-script locales. */
export const FOLDER_UPLOAD_PANEL_COPY_WESTERN: Record<
  'en' | 'es' | 'pt-BR' | 'fr' | 'it' | 'de' | 'ru' | 'vi' | 'id',
  FolderUploadPanelCopy
> = {
  en: {
    regionLabel: 'Uploads',
    filterLabel: 'Files to show',
    titleUploading: 'Uploading to {folder}',
    titleDone: 'Uploaded to {folder}',
    titleFailed: "Some files didn't upload to {folder}",
    titleCancelled: 'Upload to {folder} cancelled',
    titleCancelling: 'Cancelling upload to {folder}…',
    titleBlocked: "Couldn't upload to {folder}",
    progressSummary: '{done} of {total} files · {bytesDone} of {bytesTotal}',
    timeLeft: '~{time} left',
    summaryUploaded: '{count} uploaded',
    summaryFailed: '{count} failed',
    summaryCancelled: '{count} cancelled',
    view: 'View',
    showFiles: 'Show files',
    hideFiles: 'Hide files',
    cancelAll: 'Cancel all',
    retryFailed: 'Retry failed',
    retrySome: 'Retry {count}',
    dismiss: 'Dismiss',
    cancelFile: 'Cancel {name}',
    retryFile: 'Retry {name}',
    filterAll: 'All',
    filterFailed: 'Failed ({count})',
    statusQueued: 'Waiting',
    statusUploading: '{loaded} of {total}',
    statusRegistering: 'Finishing…',
    statusDone: 'Uploaded',
    statusCancelled: 'Upload cancelled',
    emptyFoldersFailed: 'Empty folders not created: {count}',
    progressLabel: 'Upload progress',
    pasteMenuItem: 'Paste',
    pastedImageName: 'Pasted image {date}',
    pastedTextName: 'Pasted text {date}',
    reasons: {
      storage_limit: 'Not enough workspace storage',
      uploads_busy: 'Too many uploads in progress',
      network: 'Network interrupted',
      unverified: "Changed during upload or couldn't be verified",
      permission: "You can't add files to this folder",
      invalid_path: "This folder name can't be used",
      unreadable: "Couldn't read this file",
      too_large: 'Too large to upload',
      active_content: "Its contents don't match its file type",
      workspace_loading: 'Workspace was still loading. Try again.',
      unknown: 'Upload failed',
    },
  },
  es: {
    regionLabel: 'Subidas',
    filterLabel: 'Archivos que mostrar',
    titleUploading: 'Subiendo a {folder}',
    titleDone: 'Subido a {folder}',
    titleFailed: 'Algunos archivos no se subieron a {folder}',
    titleCancelled: 'Subida a {folder} cancelada',
    titleCancelling: 'Cancelando la subida a {folder}…',
    titleBlocked: 'No se pudo subir a {folder}',
    progressSummary: '{done} de {total} archivos · {bytesDone} de {bytesTotal}',
    timeLeft: 'Quedan ~{time}',
    summaryUploaded: '{count} subidos',
    summaryFailed: '{count} con error',
    summaryCancelled: '{count} cancelados',
    view: 'Ver',
    showFiles: 'Mostrar archivos',
    hideFiles: 'Ocultar archivos',
    cancelAll: 'Cancelar todo',
    retryFailed: 'Reintentar fallidos',
    retrySome: 'Reintentar {count}',
    dismiss: 'Cerrar',
    cancelFile: 'Cancelar {name}',
    retryFile: 'Reintentar {name}',
    filterAll: 'Todos',
    filterFailed: 'Con error ({count})',
    statusQueued: 'En espera',
    statusUploading: '{loaded} de {total}',
    statusRegistering: 'Finalizando…',
    statusDone: 'Subido',
    statusCancelled: 'Subida cancelada',
    emptyFoldersFailed: 'Carpetas vacías no creadas: {count}',
    progressLabel: 'Progreso de la subida',
    pasteMenuItem: 'Pegar',
    pastedImageName: 'Imagen pegada {date}',
    pastedTextName: 'Texto pegado {date}',
    reasons: {
      storage_limit:
        'No hay suficiente almacenamiento en el espacio de trabajo',
      uploads_busy: 'Hay demasiadas subidas en curso',
      network: 'Conexión interrumpida',
      unverified: 'Cambió durante la subida o no se pudo verificar',
      permission: 'No puedes añadir archivos a esta carpeta',
      invalid_path: 'No se puede usar este nombre de carpeta',
      unreadable: 'No se pudo leer este archivo',
      too_large: 'Es demasiado grande para subirlo',
      active_content: 'El contenido no coincide con el tipo de archivo',
      workspace_loading:
        'El espacio de trabajo aún se estaba cargando. Inténtalo de nuevo.',
      unknown: 'Error al subir',
    },
  },
  'pt-BR': {
    regionLabel: 'Envios',
    filterLabel: 'Arquivos a mostrar',
    titleUploading: 'Enviando para {folder}',
    titleDone: 'Enviado para {folder}',
    titleFailed: 'Alguns arquivos não foram enviados para {folder}',
    titleCancelled: 'Envio para {folder} cancelado',
    titleCancelling: 'Cancelando o envio para {folder}…',
    titleBlocked: 'Não foi possível enviar para {folder}',
    progressSummary: '{done} de {total} arquivos · {bytesDone} de {bytesTotal}',
    timeLeft: 'Faltam ~{time}',
    summaryUploaded: '{count} enviados',
    summaryFailed: '{count} com falha',
    summaryCancelled: '{count} cancelados',
    view: 'Ver',
    showFiles: 'Mostrar arquivos',
    hideFiles: 'Ocultar arquivos',
    cancelAll: 'Cancelar tudo',
    retryFailed: 'Tentar novamente os com falha',
    retrySome: 'Tentar {count} de novo',
    dismiss: 'Fechar',
    cancelFile: 'Cancelar {name}',
    retryFile: 'Tentar {name} novamente',
    filterAll: 'Todos',
    filterFailed: 'Com falha ({count})',
    statusQueued: 'Aguardando',
    statusUploading: '{loaded} de {total}',
    statusRegistering: 'Finalizando…',
    statusDone: 'Enviado',
    statusCancelled: 'Envio cancelado',
    emptyFoldersFailed: 'Pastas vazias não criadas: {count}',
    progressLabel: 'Progresso do envio',
    pasteMenuItem: 'Colar',
    pastedImageName: 'Imagem colada {date}',
    pastedTextName: 'Texto colado {date}',
    reasons: {
      storage_limit: 'Armazenamento insuficiente no espaço de trabalho',
      uploads_busy: 'Há envios demais em andamento',
      network: 'Conexão interrompida',
      unverified: 'Mudou durante o envio ou não pôde ser verificado',
      permission: 'Você não pode adicionar arquivos a esta pasta',
      invalid_path: 'Este nome de pasta não pode ser usado',
      unreadable: 'Não foi possível ler este arquivo',
      too_large: 'Grande demais para enviar',
      active_content: 'O conteúdo não corresponde ao tipo do arquivo',
      workspace_loading:
        'O espaço de trabalho ainda estava carregando. Tente novamente.',
      unknown: 'Falha no envio',
    },
  },
  fr: {
    regionLabel: 'Téléversements',
    filterLabel: 'Fichiers à afficher',
    titleUploading: 'Téléversement vers {folder}',
    titleDone: 'Téléversé vers {folder}',
    titleFailed: "Certains fichiers n'ont pas été téléversés vers {folder}",
    titleCancelled: 'Téléversement vers {folder} annulé',
    titleCancelling: 'Annulation du téléversement vers {folder}…',
    titleBlocked: 'Impossible de téléverser vers {folder}',
    progressSummary:
      '{done} sur {total} fichiers · {bytesDone} sur {bytesTotal}',
    timeLeft: 'Encore ~{time}',
    summaryUploaded: '{count} téléversés',
    summaryFailed: '{count} en échec',
    summaryCancelled: '{count} annulés',
    view: 'Voir',
    showFiles: 'Afficher les fichiers',
    hideFiles: 'Masquer les fichiers',
    cancelAll: 'Tout annuler',
    retryFailed: 'Réessayer les échecs',
    retrySome: 'Réessayer {count}',
    dismiss: 'Fermer',
    cancelFile: 'Annuler {name}',
    retryFile: 'Réessayer {name}',
    filterAll: 'Tous',
    filterFailed: 'En échec ({count})',
    statusQueued: 'En attente',
    statusUploading: '{loaded} sur {total}',
    statusRegistering: 'Finalisation…',
    statusDone: 'Téléversé',
    statusCancelled: 'Téléversement annulé',
    emptyFoldersFailed: 'Dossiers vides non créés : {count}',
    progressLabel: 'Progression du téléversement',
    pasteMenuItem: 'Coller',
    pastedImageName: 'Image collée {date}',
    pastedTextName: 'Texte collé {date}',
    reasons: {
      storage_limit: "Pas assez d'espace de stockage dans l'espace de travail",
      uploads_busy: 'Trop de téléversements en cours',
      network: 'Connexion interrompue',
      unverified: 'Modifié pendant le téléversement ou impossible à vérifier',
      permission: 'Vous ne pouvez pas ajouter de fichiers à ce dossier',
      invalid_path: 'Ce nom de dossier ne peut pas être utilisé',
      unreadable: 'Impossible de lire ce fichier',
      too_large: 'Trop volumineux pour être téléversé',
      active_content: 'Le contenu ne correspond pas au type de fichier',
      workspace_loading:
        "L'espace de travail était encore en cours de chargement. Réessayez.",
      unknown: 'Échec du téléversement',
    },
  },
  it: {
    regionLabel: 'Caricamenti',
    filterLabel: 'File da mostrare',
    titleUploading: 'Caricamento in {folder}',
    titleDone: 'Caricato in {folder}',
    titleFailed: 'Alcuni file non sono stati caricati in {folder}',
    titleCancelled: 'Caricamento in {folder} annullato',
    titleCancelling: 'Annullamento del caricamento in {folder}…',
    titleBlocked: 'Impossibile caricare in {folder}',
    progressSummary: '{done} di {total} file · {bytesDone} di {bytesTotal}',
    timeLeft: 'Mancano ~{time}',
    summaryUploaded: '{count} caricati',
    summaryFailed: '{count} non riusciti',
    summaryCancelled: '{count} annullati',
    view: 'Visualizza',
    showFiles: 'Mostra file',
    hideFiles: 'Nascondi file',
    cancelAll: 'Annulla tutto',
    retryFailed: 'Riprova i non riusciti',
    retrySome: 'Riprova {count}',
    dismiss: 'Chiudi',
    cancelFile: 'Annulla {name}',
    retryFile: 'Riprova {name}',
    filterAll: 'Tutti',
    filterFailed: 'Non riusciti ({count})',
    statusQueued: 'In attesa',
    statusUploading: '{loaded} di {total}',
    statusRegistering: 'Completamento…',
    statusDone: 'Caricato',
    statusCancelled: 'Caricamento annullato',
    emptyFoldersFailed: 'Cartelle vuote non create: {count}',
    progressLabel: 'Avanzamento del caricamento',
    pasteMenuItem: 'Incolla',
    pastedImageName: 'Immagine incollata {date}',
    pastedTextName: 'Testo incollato {date}',
    reasons: {
      storage_limit:
        "Spazio di archiviazione dell'area di lavoro insufficiente",
      uploads_busy: 'Troppi caricamenti in corso',
      network: 'Connessione interrotta',
      unverified: 'Modificato durante il caricamento o non verificabile',
      permission: 'Non puoi aggiungere file a questa cartella',
      invalid_path: 'Questo nome di cartella non può essere usato',
      unreadable: 'Impossibile leggere questo file',
      too_large: 'Troppo grande da caricare',
      active_content: 'Il contenuto non corrisponde al tipo di file',
      workspace_loading: "L'area di lavoro era ancora in caricamento. Riprova.",
      unknown: 'Caricamento non riuscito',
    },
  },
  de: {
    regionLabel: 'Uploads',
    filterLabel: 'Angezeigte Dateien',
    titleUploading: 'Wird in {folder} hochgeladen',
    titleDone: 'In {folder} hochgeladen',
    titleFailed: 'Einige Dateien wurden nicht in {folder} hochgeladen',
    titleCancelled: 'Upload in {folder} abgebrochen',
    titleCancelling: 'Upload nach {folder} wird abgebrochen…',
    titleBlocked: 'Upload in {folder} nicht möglich',
    progressSummary:
      '{done} von {total} Dateien · {bytesDone} von {bytesTotal}',
    timeLeft: 'Noch ~{time}',
    summaryUploaded: '{count} hochgeladen',
    summaryFailed: '{count} fehlgeschlagen',
    summaryCancelled: '{count} abgebrochen',
    view: 'Anzeigen',
    showFiles: 'Dateien anzeigen',
    hideFiles: 'Dateien ausblenden',
    cancelAll: 'Alle abbrechen',
    retryFailed: 'Fehlgeschlagene wiederholen',
    retrySome: '{count} erneut versuchen',
    dismiss: 'Schließen',
    cancelFile: '{name} abbrechen',
    retryFile: '{name} erneut versuchen',
    filterAll: 'Alle',
    filterFailed: 'Fehlgeschlagen ({count})',
    statusQueued: 'Wartet',
    statusUploading: '{loaded} von {total}',
    statusRegistering: 'Wird abgeschlossen…',
    statusDone: 'Hochgeladen',
    statusCancelled: 'Upload abgebrochen',
    emptyFoldersFailed: 'Leere Ordner nicht erstellt: {count}',
    progressLabel: 'Upload-Fortschritt',
    pasteMenuItem: 'Einfügen',
    pastedImageName: 'Eingefügtes Bild {date}',
    pastedTextName: 'Eingefügter Text {date}',
    reasons: {
      storage_limit: 'Nicht genug Speicherplatz im Workspace',
      uploads_busy: 'Zu viele Uploads gleichzeitig',
      network: 'Verbindung unterbrochen',
      unverified: 'Während des Uploads geändert oder nicht überprüfbar',
      permission: 'Du kannst diesem Ordner keine Dateien hinzufügen',
      invalid_path: 'Dieser Ordnername kann nicht verwendet werden',
      unreadable: 'Diese Datei konnte nicht gelesen werden',
      too_large: 'Zu groß zum Hochladen',
      active_content: 'Der Inhalt passt nicht zum Dateityp',
      workspace_loading:
        'Der Workspace wurde noch geladen. Versuche es erneut.',
      unknown: 'Upload fehlgeschlagen',
    },
  },
  ru: {
    regionLabel: 'Загрузки',
    filterLabel: 'Какие файлы показать',
    titleUploading: 'Загрузка в {folder}',
    titleDone: 'Загружено в {folder}',
    titleFailed: 'Некоторые файлы не загрузились в {folder}',
    titleCancelled: 'Загрузка в {folder} отменена',
    titleCancelling: 'Отмена загрузки в {folder}…',
    titleBlocked: 'Не удалось загрузить в {folder}',
    progressSummary: '{done} из {total} файлов · {bytesDone} из {bytesTotal}',
    timeLeft: 'Осталось ~{time}',
    summaryUploaded: 'Загружено: {count}',
    summaryFailed: 'Ошибок: {count}',
    summaryCancelled: 'Отменено: {count}',
    view: 'Показать',
    showFiles: 'Показать файлы',
    hideFiles: 'Скрыть файлы',
    cancelAll: 'Отменить все',
    retryFailed: 'Повторить неудачные',
    retrySome: 'Повторить {count}',
    dismiss: 'Закрыть',
    cancelFile: 'Отменить {name}',
    retryFile: 'Повторить {name}',
    filterAll: 'Все',
    filterFailed: 'С ошибкой ({count})',
    statusQueued: 'В очереди',
    statusUploading: '{loaded} из {total}',
    statusRegistering: 'Завершение…',
    statusDone: 'Загружено',
    statusCancelled: 'Загрузка отменена',
    emptyFoldersFailed: 'Пустые папки не созданы: {count}',
    progressLabel: 'Ход загрузки',
    pasteMenuItem: 'Вставить',
    pastedImageName: 'Вставленное изображение {date}',
    pastedTextName: 'Вставленный текст {date}',
    reasons: {
      storage_limit: 'Недостаточно места в рабочем пространстве',
      uploads_busy: 'Слишком много загрузок одновременно',
      network: 'Соединение прервано',
      unverified: 'Изменился во время загрузки или не прошёл проверку',
      permission: 'Вы не можете добавлять файлы в эту папку',
      invalid_path: 'Это имя папки использовать нельзя',
      unreadable: 'Не удалось прочитать этот файл',
      too_large: 'Слишком большой для загрузки',
      active_content: 'Содержимое не соответствует типу файла',
      workspace_loading:
        'Рабочее пространство ещё загружалось. Попробуйте снова.',
      unknown: 'Ошибка загрузки',
    },
  },
  vi: {
    regionLabel: 'Tải lên',
    filterLabel: 'Tệp cần hiển thị',
    titleUploading: 'Đang tải lên {folder}',
    titleDone: 'Đã tải lên {folder}',
    titleFailed: 'Một số tệp chưa được tải lên {folder}',
    titleCancelled: 'Đã hủy tải lên {folder}',
    titleCancelling: 'Đang hủy tải lên {folder}…',
    titleBlocked: 'Không thể tải lên {folder}',
    progressSummary: '{done}/{total} tệp · {bytesDone}/{bytesTotal}',
    timeLeft: 'Còn ~{time}',
    summaryUploaded: 'Đã tải lên {count}',
    summaryFailed: '{count} lỗi',
    summaryCancelled: 'Đã hủy {count}',
    view: 'Xem',
    showFiles: 'Hiện tệp',
    hideFiles: 'Ẩn tệp',
    cancelAll: 'Hủy tất cả',
    retryFailed: 'Thử lại tệp lỗi',
    retrySome: 'Thử lại {count}',
    dismiss: 'Đóng',
    cancelFile: 'Hủy {name}',
    retryFile: 'Thử lại {name}',
    filterAll: 'Tất cả',
    filterFailed: 'Lỗi ({count})',
    statusQueued: 'Đang chờ',
    statusUploading: '{loaded}/{total}',
    statusRegistering: 'Đang hoàn tất…',
    statusDone: 'Đã tải lên',
    statusCancelled: 'Đã hủy tải lên',
    emptyFoldersFailed: 'Thư mục trống chưa được tạo: {count}',
    progressLabel: 'Tiến độ tải lên',
    pasteMenuItem: 'Dán',
    pastedImageName: 'Ảnh đã dán {date}',
    pastedTextName: 'Văn bản đã dán {date}',
    reasons: {
      storage_limit: 'Không đủ dung lượng lưu trữ của không gian làm việc',
      uploads_busy: 'Có quá nhiều lượt tải lên đang chạy',
      network: 'Kết nối bị gián đoạn',
      unverified: 'Đã thay đổi trong khi tải lên hoặc không thể xác minh',
      permission: 'Bạn không thể thêm tệp vào thư mục này',
      invalid_path: 'Không thể dùng tên thư mục này',
      unreadable: 'Không đọc được tệp này',
      too_large: 'Quá lớn để tải lên',
      active_content: 'Nội dung không khớp với loại tệp',
      workspace_loading: 'Không gian làm việc vẫn đang tải. Hãy thử lại.',
      unknown: 'Tải lên thất bại',
    },
  },
  id: {
    regionLabel: 'Unggahan',
    filterLabel: 'File yang ditampilkan',
    titleUploading: 'Mengunggah ke {folder}',
    titleDone: 'Diunggah ke {folder}',
    titleFailed: 'Beberapa file tidak terunggah ke {folder}',
    titleCancelled: 'Unggahan ke {folder} dibatalkan',
    titleCancelling: 'Membatalkan unggahan ke {folder}…',
    titleBlocked: 'Tidak dapat mengunggah ke {folder}',
    progressSummary: '{done} dari {total} file · {bytesDone} dari {bytesTotal}',
    timeLeft: 'Sisa ~{time}',
    summaryUploaded: '{count} terunggah',
    summaryFailed: '{count} gagal',
    summaryCancelled: '{count} dibatalkan',
    view: 'Lihat',
    showFiles: 'Tampilkan file',
    hideFiles: 'Sembunyikan file',
    cancelAll: 'Batalkan semua',
    retryFailed: 'Coba lagi yang gagal',
    retrySome: 'Coba lagi {count}',
    dismiss: 'Tutup',
    cancelFile: 'Batalkan {name}',
    retryFile: 'Coba lagi {name}',
    filterAll: 'Semua',
    filterFailed: 'Gagal ({count})',
    statusQueued: 'Menunggu',
    statusUploading: '{loaded} dari {total}',
    statusRegistering: 'Menyelesaikan…',
    statusDone: 'Terunggah',
    statusCancelled: 'Unggahan dibatalkan',
    emptyFoldersFailed: 'Folder kosong tidak dibuat: {count}',
    progressLabel: 'Progres unggahan',
    pasteMenuItem: 'Tempel',
    pastedImageName: 'Gambar ditempel {date}',
    pastedTextName: 'Teks ditempel {date}',
    reasons: {
      storage_limit: 'Penyimpanan ruang kerja tidak cukup',
      uploads_busy: 'Terlalu banyak unggahan yang sedang berjalan',
      network: 'Koneksi terputus',
      unverified: 'Berubah saat diunggah atau tidak dapat diverifikasi',
      permission: 'Anda tidak dapat menambahkan file ke folder ini',
      invalid_path: 'Nama folder ini tidak dapat digunakan',
      unreadable: 'File ini tidak dapat dibaca',
      too_large: 'Terlalu besar untuk diunggah',
      active_content: 'Isinya tidak cocok dengan jenis file',
      workspace_loading: 'Ruang kerja masih dimuat. Coba lagi.',
      unknown: 'Unggahan gagal',
    },
  },
};
